import Link from "next/link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { getEmptyListState } from "@/core/ui/empty-states";
import type { getConsoleSnapshot } from "@/lib/demo-data";
import type { ManagerAnalytics } from "@/core/console/manager-analytics";

type Snapshot = ReturnType<typeof getConsoleSnapshot>;

// Phase 9.G — the deferred retrospective KPIs. Explicitly NOT the home
// screen: the agenda is for "now", this is the manager's "how are we
// doing" digest. Minimal by design — margin by customer/lane/carrier
// plus a quiet stat line. No load board, no action wall.
export function InsightsView({ snapshot, analytics }: { snapshot: Snapshot; analytics?: ManagerAnalytics }) {
  const openExceptions = snapshot.loads.flatMap((item) => item.exceptions).length;
  const avgHealth = Math.round(
    snapshot.loads.length === 0
      ? 0
      : snapshot.loads.reduce((t, i) => t + i.confidence.loadHealthScore, 0) /
          snapshot.loads.length,
  );

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-8 px-6 py-10">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Insights</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Retrospective performance. For what needs you right now, see the{" "}
            <Link className="underline hover:text-foreground" href="/">
              agenda
            </Link>
            .
          </p>
        </div>
      </header>

      {analytics ? (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Kpi label="Loads under mgmt" value={String(analytics.loadsActive)} sub={`${analytics.loadsTotal} total`} />
          <Kpi label="Delivered" value={String(analytics.loadsDelivered)} />
          <Kpi label="Exceptions" value={String(analytics.exceptionsDetected)} sub={`${analytics.exceptionsOpen} open`} />
          <Kpi
            label="Resolution rate"
            value={`${analytics.exceptionResolutionRate}%`}
            sub={`${analytics.criticalExceptionsOpen} critical open`}
          />
          <Kpi label="Approval rate" value={`${analytics.approvalRate}%`} sub={`${analytics.approvalsDecided} decided`} />
          <Kpi label="Pending" value={String(analytics.approvalsPending)} sub="approvals" />
        </section>
      ) : null}

      <section className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
        <Stat label="Active loads" value={String(snapshot.loads.length)} />
        <Stat label="Open exceptions" value={String(openExceptions)} />
        <Stat label="Avg health" value={`${avgHealth}%`} />
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <MarginTable title="By customer" rows={snapshot.marginReports.byCustomer} />
        <MarginTable title="By lane" rows={snapshot.marginReports.byLane} />
        <MarginTable title="By carrier" rows={snapshot.marginReports.byCarrier} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs font-medium">{label}</div>
      {sub ? <div className="text-[11px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function MarginTable({
  title,
  rows,
}: {
  title: string;
  rows: Snapshot["marginReports"]["byCustomer"];
}) {
  return (
    <Card>
      <CardHeader className="p-3">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 p-3">
        {rows.length === 0 ? (
          <EmptyState className="p-4" {...getEmptyListState("marginRows")} />
        ) : (
          rows.slice(0, 5).map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-[1fr_auto] gap-3 rounded-md border border-border p-2 text-sm"
            >
              <div>
                <div className="font-medium">{row.key}</div>
                <div className="text-xs text-muted-foreground">{row.loadCount} loads</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{formatMoney(row.marginCents)}</div>
                <div className="text-xs text-muted-foreground">{row.marginPct}%</div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
